import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Clock, 
  Search,
  Filter,
  Building2,
  Stethoscope,
  Mail,
  Phone,
  Eye,
  Shield,
  CheckCircle,
  XCircle,
  Plus,
  X,
  MailCheck,
  UserCog,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient as api } from '../lib/api';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import { OpenTicketsBadge } from '../components/support/OpenTicketsBadge';
import { toast } from '../lib/toast';
import InviteUserModal from '../components/users/InviteUserModal';
import InvitationsViewer from '../components/users/InvitationsViewer';
import PhysicianIDProofingViewer from '../components/physicians/PhysicianIDProofingViewer';
import { AdminsListView } from '../components/admins/AdminsListView';
import { useHierarchyMoves } from '../components/users/useHierarchyMoves';
import { UserAccessModal } from '../components/users/UserAccessModal';
import { User } from '@bloom/shared';
import { formatDate as formatDateUtil } from '../utils/dateUtils';

interface UserDisplay extends Omit<User, 'createdAt' | 'updatedAt'> {
  _id: string;
  createdAt: string;
  updatedAt: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  suspendedUsers: number;
  physicians: number;
  distributors: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

const UserManagement: React.FC = () => {
  const user = useUser();
  const location = useLocation();
  const [users, setUsers] = useState<UserDisplay[]>([]);
  const [physicianTicketCounts, setPhysicianTicketCounts] = useState<Record<string, number>>({});
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // What the list was last asked for: typing is debounced so each keystroke
  // doesn't fire a request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  
  // AM filter (admin/supervisor filtering physicians by account manager). A
  // server-searched picker: there are more AMs than one page holds, so the old
  // load-500-and-filter-in-the-browser dropdown couldn't find the rest.
  const [selectedAm, setSelectedAm] = useState<User | null>(null);
  const amFilter = selectedAm?._id ?? '';
  // Sequence number of the latest list request; older responses are dropped.
  const fetchSeq = React.useRef(0);
  const lastQuery = React.useRef('');
  const [selectedUser, setSelectedUser] = useState<UserDisplay | null>(null);
  const [showAccessModal, setShowAccessModal] = useState(false);

  // Percent-markup mode admin controls (supervisor / AM only)

  // Modal: collapsible sections and downstream user lists

  // Team member state (supervisor multi-login)
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showInvitationsViewer, setShowInvitationsViewer] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [showIDProofingViewer, setShowIDProofingViewer] = useState(false);
  const [idProofingPhysicianId, setIdProofingPhysicianId] = useState<string | null>(null);
  
  // Supervisor / AM moves raised from the detail modal (shared with All Users).
  const hierarchyMoves = useHierarchyMoves(selectedUser, () => fetchData());

  // Impersonation state
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonationReason, setImpersonationReason] = useState('');
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);

  // Suspend confirmation state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState<UserDisplay | null>(null);

  // Unsuspend confirmation state
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [userToUnsuspend, setUserToUnsuspend] = useState<UserDisplay | null>(null);

  // Billing configuration state (for physicians)
  const [_creditStatusLoading, _setCreditStatusLoading] = useState(false);

  // Determine the current view based on the route
  const getCurrentView = (): 'pending' | 'physicians' | 'distributors' | 'supervisors' | 'admins' => {
    if (location.pathname === '/supervisors') {
      return 'supervisors';
    } else if (location.pathname === '/distributors') {
      return 'distributors';
    } else if (location.pathname === '/physicians') {
      return 'physicians';
    } else if (location.pathname === '/admins') {
      return 'admins';
    } else if (location.pathname === '/pending') {
      return 'pending';
    } else {
      return 'pending'; // Default fallback
    }
  };

  const currentView = getCurrentView();

  // Check if user has access (admin, supervisor, or distributor)
  if (!user || (user.role !== 'admin' && user.role !== 'supervisor' && user.role !== 'distributor')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Check access for supervisors page (admin only)
  if (currentView === 'supervisors' && user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access supervisor management.</p>
        </div>
      </div>
    );
  }

  // Check distributor access for distributors page
  if (currentView === 'distributors' && user.role !== 'admin' && user.role !== 'supervisor') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access account manager management.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch data. A new search or filter goes back to page 1 *instead of*
  // fetching, so the old page is never requested for the new question (two
  // separate effects used to fire both requests and race).
  useEffect(() => {
    const queryKey = JSON.stringify([currentView, statusFilter, debouncedSearch, amFilter]);
    if (lastQuery.current !== queryKey) {
      lastQuery.current = queryKey;
      if (currentPage !== 1) { fetchSeq.current++; setCurrentPage(1); return; }
    }
    fetchData();
  }, [currentView, statusFilter, debouncedSearch, amFilter, currentPage]);

  const fetchData = async () => {
    const mine = ++fetchSeq.current;
    const isStale = () => mine !== fetchSeq.current;
    try {
      setLoading(true);
      
      // Only fetch stats for admin users (not supervisor — stats endpoint is admin-only)
      if (user?.role === 'admin') {
        const statsResponse = await api.get('/users/stats');
        if (statsResponse.success) {
          setStats(statsResponse.data);
        }
      }

      // Fetch users based on current view
      let endpoint = '';
      const params: Record<string, string> = {};
      
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      
      switch (currentView) {
        case 'pending':
          endpoint = '/users/pending';
          break;
        case 'physicians':
          endpoint = '/users/physicians';
          // Add pagination params for physicians
          params.page = currentPage.toString();
          params.limit = pageSize.toString();
          // Add AM filter for admin and supervisor
          if (amFilter && (user?.role === 'admin' || user?.role === 'supervisor')) {
            params.distributorId = amFilter;
          }
          break;
        case 'distributors':
          endpoint = '/users/distributors';
          // Add pagination params for distributors
          params.page = currentPage.toString();
          params.limit = pageSize.toString();
          break;
        case 'supervisors':
          endpoint = '/users/supervisors';
          params.page = currentPage.toString();
          params.limit = pageSize.toString();
          break;
      }

      const queryString = new URLSearchParams(params).toString();
      const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
      const response = await api.get(fullEndpoint);
      // A newer request (different page, search or filter) has superseded this one.
      if (isStale()) return;
      if (response.success) {
        // Handle paginated response for physicians and distributors
        let loadedUsers: any[] = [];
        if ((currentView === 'physicians' || currentView === 'distributors' || currentView === 'supervisors') && response.data?.data && response.data?.pagination) {
          loadedUsers = response.data.data;
          setUsers(loadedUsers);
          setPagination(response.data.pagination);
        } else {
          // Non-paginated response
          loadedUsers = response.data?.data || response.data || [];
          setUsers(loadedUsers);
          setPagination(null);
        }

        // Bulk-fetch open-ticket counts for physicians on screen so the
        // badge column doesn't issue one HTTP request per row.
        if (currentView === 'physicians' && user?.role === 'admin' && loadedUsers.length > 0) {
          const ids = loadedUsers
            .filter((u: any) => u.role === 'physician' || u.role === 'physician_readonly')
            .map((u: any) => u._id);
          api.getOpenTicketCountsByPhysician(ids).then(res => {
            if (!isStale() && res.success && res.data) {
              setPhysicianTicketCounts(res.data.counts);
            }
          });
        } else {
          setPhysicianTicketCounts({});
        }
      }
    } catch (error) {
      if (!isStale()) console.error('Error fetching data:', error);
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPaginationControls = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page, totalPages, totalCount, limit } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalCount);

    // Generate page numbers to show
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (page >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = page - 1; i <= page + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startItem}</span> to{' '}
              <span className="font-medium">{endItem}</span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pageNumbers.map((pageNum, index) => (
                pageNum === '...' ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum as number)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === pageNum
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              ))}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const handleApproveUser = async (userId: string, userRole: string) => {
    try {
      setProcessingAction(userId);
      const endpoint = userRole === 'physician' 
        ? `/users/physicians/${userId}/approve`
        : `/users/distributors/${userId}/approve`;
      
      const response = await api.put(endpoint);
      
      if (response.success) {
        // Refresh data
        fetchData();
        // Show success message
        alert('User approved successfully');
      }
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user');
    } finally {
      setProcessingAction(null);
    }
  };

  // Open suspend confirmation modal
  const handleOpenSuspendModal = (userItem: UserDisplay) => {
    setUserToSuspend(userItem);
    setShowSuspendModal(true);
  };

  // Cancel suspend
  const handleCancelSuspend = () => {
    setShowSuspendModal(false);
    setUserToSuspend(null);
  };

  // Confirm and execute suspend
  const handleConfirmSuspend = async () => {
    if (!userToSuspend) return;
    
    try {
      setProcessingAction(userToSuspend._id);
      const endpoint = userToSuspend.role === 'physician' || userToSuspend.role === 'physician_readonly'
        ? `/users/physicians/${userToSuspend._id}/suspend`
        : `/users/distributors/${userToSuspend._id}/suspend`;

      const response = await api.put(endpoint);

      if (response.success) {
        // Refresh data
        fetchData();
        // Show success message
        toast.success('User suspended successfully');
        // Close modal
        setShowSuspendModal(false);
        setUserToSuspend(null);
      } else {
        toast.error(response.error || 'Failed to suspend user');
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error('Failed to suspend user');
    } finally {
      setProcessingAction(null);
    }
  };

  // Open unsuspend confirmation modal
  const handleOpenUnsuspendModal = (userItem: UserDisplay) => {
    setUserToUnsuspend(userItem);
    setShowUnsuspendModal(true);
  };

  // Cancel unsuspend
  const handleCancelUnsuspend = () => {
    setShowUnsuspendModal(false);
    setUserToUnsuspend(null);
  };

  // Confirm and execute unsuspend
  const handleConfirmUnsuspend = async () => {
    if (!userToUnsuspend) return;

    try {
      setProcessingAction(userToUnsuspend._id);
      const endpoint = userToUnsuspend.role === 'physician' || userToUnsuspend.role === 'physician_readonly'
        ? `/users/physicians/${userToUnsuspend._id}/unsuspend`
        : `/users/distributors/${userToUnsuspend._id}/unsuspend`;

      const response = await api.put(endpoint);

      if (response.success) {
        // Refresh data
        fetchData();
        // Show success message
        toast.success('User reactivated successfully');
        // Close modal
        setShowUnsuspendModal(false);
        setUserToUnsuspend(null);
      } else {
        toast.error(response.error || 'Failed to reactivate user');
      }
    } catch (error) {
      console.error('Error reactivating user:', error);
      toast.error('Failed to reactivate user');
    } finally {
      setProcessingAction(null);
    }
  };






  /**
   * Open the detail modal. The modal fetches whatever the role needs itself
   * (see UserAccessModal), so this no longer duplicates that work — it only
   * chooses who to show.
   */
  const handleViewUserDetails = (userItem: UserDisplay) => {
    setSelectedUser(userItem);
    setShowAccessModal(true);
  };


  const handleCloseAccessModal = () => {
    setShowAccessModal(false);
    // Modal-local state now lives in UserAccessModal and unmounts with it.
    // The move-confirmation modal is a sibling of the detail modal, so reset it too.
    hierarchyMoves.cancel();
  };



  // Handle opening impersonation modal
  const handleOpenImpersonateModal = () => {
    setShowImpersonateModal(true);
    setImpersonationReason('');
    setImpersonationError(null);
  };

  // Handle canceling impersonation
  const handleCancelImpersonation = () => {
    setShowImpersonateModal(false);
    setImpersonationReason('');
    setImpersonationError(null);
  };

  // Handle starting impersonation
  const handleStartImpersonation = async () => {
    if (!selectedUser) return;

    try {
      setImpersonationLoading(true);
      setImpersonationError(null);

      const response = await api.startImpersonation(selectedUser._id, impersonationReason || undefined);

      if (response.success && response.data) {
        // Save the admin's current token so we can restore it when stopping impersonation
        const currentToken = localStorage.getItem('bloom_token');
        if (currentToken) {
          localStorage.setItem('bloom_admin_token', currentToken);
        }

        // Store the new impersonation token - MUST use 'bloom_token' which is what the API client reads
        localStorage.setItem('bloom_token', response.data.token);

        // CRITICAL: Clear ALL cached auth state to force fresh load
        localStorage.removeItem('bloom-auth');
        sessionStorage.clear();

        // Force a complete page reload - this ensures:
        // 1. Zustand reinitializes from scratch
        // 2. App calls /me with new impersonation token
        // 3. UI renders with impersonated user's role
        window.location.replace('/dashboard');
      } else {
        setImpersonationError(response.error || 'Failed to start impersonation');
      }
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      setImpersonationError('An unexpected error occurred');
    } finally {
      setImpersonationLoading(false);
    }
  };

  const formatDate = (dateString: string) => formatDateUtil(dateString);


  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      suspended: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Suspended' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config?.icon || Clock;
    const label = config?.label || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color || 'bg-gray-100 text-gray-800'}`}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </span>
    );
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'physician':
      case 'physician_readonly':
        return <Stethoscope className="w-4 h-4 text-blue-600" />;
      case 'supervisor':
        return <Shield className="w-4 h-4 text-orange-600" />;
      case 'distributor':
        return <Building2 className="w-4 h-4 text-purple-600" />;
      default:
        return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  const getPageTitle = () => {
    switch (currentView) {
      case 'pending':
        return user?.role === 'supervisor' ? 'Pending Account Managers' : 'Pending Approvals';
      case 'physicians':
        return (user?.role === 'distributor' || user?.role === 'supervisor') ? 'My Physicians' : 'Physician Management';
      case 'distributors':
        return user?.role === 'supervisor' ? 'My Account Managers' : 'Account Manager Management';
      case 'supervisors':
        return 'Supervisor Management';
      default:
        return 'User Management';
    }
  };

  const getPageDescription = () => {
    switch (currentView) {
      case 'pending':
        return user?.role === 'supervisor'
          ? 'Review and approve pending account managers you invited'
          : 'Review and approve pending user registrations';
      case 'physicians':
        return (user?.role === 'distributor' || user?.role === 'supervisor')
          ? 'Manage physicians in your network'
          : 'Manage physician accounts and permissions';
      case 'distributors':
        return user?.role === 'supervisor'
          ? 'Manage account managers in your network'
          : 'Manage account manager accounts and permissions';
      case 'supervisors':
        return 'Manage supervisor accounts and their account manager networks';
      default:
        return 'Manage user accounts and approvals';
    }
  };

  // Helper to get AM name from physicianData (populated by backend)
  // Renders a check or X icon for a physician's verification gate.
  // ID:      physicianData.idProofingStatus === 'approved'
  // License: physicianData.credentialVerification.status === 'verified'
  // Anything else (pending / submitted / rejected / expired / revoked / missing)
  // is treated as not-yet-valid. Wrapping span carries the title for hover.
  const renderVerifyIcon = (valid: boolean, label: string, detail?: string) => {
    const tip = detail ? `${label}: ${detail}` : `${label} ${valid ? 'approved' : 'not approved'}`;
    return (
      <span title={tip} aria-label={tip} className="inline-flex">
        {valid
          ? <CheckCircle className="w-4 h-4 text-green-600" />
          : <XCircle className="w-4 h-4 text-gray-400" />}
      </span>
    );
  };

  // Second verification gate (Gate B). For a normal clinic this is the medical
  // license ('License'). A telemed-only clinic (no NPI/license) has Gate B
  // satisfied by its signed Contract + BAA, so we surface it as 'Telemed' instead
  // of showing a perpetually-unverified license.
  const renderCredentialGate = (userItem: UserDisplay) => {
    const pd = userItem.physicianData;
    if (pd?.telemedOnly) {
      const signed = pd?.idProofingData?.agreementAccepted === true;
      return (
        <span className="inline-flex items-center gap-1">
          {renderVerifyIcon(signed, 'Telemed', signed ? 'verified via signed BAA (no NPI/license)' : 'awaiting signed BAA')}
          <span className="text-xs">Telemed</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1">
        {renderVerifyIcon(pd?.credentialVerification?.status === 'verified', 'License', pd?.credentialVerification?.status)}
        <span className="text-xs">License</span>
      </span>
    );
  };

  // Compact variant for the desktop table's fixed 'License' column: telemed-only
  // clinics get a 'Telemed' pill instead of a license check.
  const renderCredentialGateCell = (userItem: UserDisplay) => {
    const pd = userItem.physicianData;
    if (pd?.telemedOnly) {
      const signed = pd?.idProofingData?.agreementAccepted === true;
      const tip = signed
        ? 'Telemed-only clinic — verified via signed BAA (no NPI/license)'
        : 'Telemed-only clinic — awaiting signed BAA';
      return (
        <span
          title={tip}
          aria-label={tip}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
            signed ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          Telemed
        </span>
      );
    }
    return renderVerifyIcon(pd?.credentialVerification?.status === 'verified', 'License', pd?.credentialVerification?.status);
  };

  // Volume-rewards tier currently in effect for a clinic. Attached read-only by
  // the physician-list endpoint (frozen from last week's paid volume) so admins/
  // AMs can see standing without impersonating.
  const renderRewardsTierCell = (userItem: UserDisplay) => {
    const rt = userItem.rewardsTier;
    const muted = (text: string, tip: string) => (
      <span className="cell-muted text-[10px]" title={tip} aria-label={tip}>{text}</span>
    );
    // Program off globally or older payload without the field: nothing to show.
    if (!rt || !rt.programActive) return muted('—', 'Volume rewards program not active');
    if (!rt.enrolled) return muted('Off', 'Not enrolled in volume rewards');
    if (!rt.tierLabel) return muted('None', 'Enrolled — no tier earned from last week’s volume');

    const tierStyles: Record<string, string> = {
      Silver: 'bg-gray-100 text-gray-700',
      Gold: 'bg-amber-100 text-amber-800',
      Platinum: 'bg-indigo-100 text-indigo-700',
      Diamond: 'bg-cyan-100 text-cyan-800'
    };
    const style = tierStyles[rt.tierLabel] || 'bg-emerald-100 text-emerald-800';
    const tip = `Volume-rewards tier in effect: ${rt.tierLabel} (earned from last week's paid volume)`;
    return (
      <span
        title={tip}
        aria-label={tip}
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${style}`}
      >
        {rt.tierLabel}
      </span>
    );
  };

  const getAccountManagerName = (userItem: UserDisplay): string => {
    const distId = userItem.physicianData?.distributorId;
    if (!distId) return '—';
    
    // distributorId is populated as an object with profile and distributorData
    if (typeof distId === 'object' && distId !== null) {
      const dist = distId as any;
      
      // Prefer company name if available
      if (dist.distributorData?.companyName) {
        return dist.distributorData.companyName;
      }
      
      // Fall back to person's name
      if (dist.profile) {
        const name = `${dist.profile.firstName || ''} ${dist.profile.lastName || ''}`.trim();
        if (name) return name;
      }
      
      return '—';
    }
    return '—';
  };

  // Helper to check if phone number looks fake (e.g., 0000000, 1111111, etc.)
  const isFakePhoneNumber = (phone: string | undefined): boolean => {
    if (!phone) return true;
    const cleaned = phone.replace(/\D/g, ''); // Remove non-digits
    if (cleaned.length < 7) return true;
    // Check if all digits are the same (e.g., 0000000000, 1111111111)
    if (/^(\d)\1+$/.test(cleaned)) return true;
    // Check for sequential patterns like 1234567890
    if (/^0{7,}$/.test(cleaned)) return true;
    return false;
  };

  // Helper to check if physician has clinic name set
  const hasClinicName = (userItem: UserDisplay): boolean => {
    return !!(userItem.profile?.clinicName && userItem.profile.clinicName.trim());
  };

  // Mobile card view for a single user
  const renderUserCard = (userItem: UserDisplay) => (
    <div key={userItem._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Header with name and status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getRoleIcon(userItem.role)}
          <div>
            <div className="text-sm font-medium text-gray-900 inline-flex items-center gap-2">
              {userItem.profile.firstName} {userItem.profile.lastName}
              {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && (
                <OpenTicketsBadge count={physicianTicketCounts[userItem._id] || 0} />
              )}
            </div>
            {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && userItem.physicianData?.npiNumber && (
              <div className="text-xs text-gray-500 font-mono">NPI: {userItem.physicianData.npiNumber}</div>
            )}
          </div>
        </div>
        {getStatusBadge(userItem.status)}
      </div>
      
      {/* Contact info */}
      <div className="space-y-1 mb-3 text-sm">
        <div className="flex items-center text-gray-600">
          <Mail className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-gray-400" />
          <span className="truncate max-w-[200px]" title={userItem.email}>{userItem.email}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Phone className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-gray-400" />
          <span>{userItem.profile.phone}</span>
          {userItem.role === 'distributor' && isFakePhoneNumber(userItem.profile.phone) && (
            <span className="ml-2 inline-flex items-center text-amber-600" title="Phone number appears invalid">
              <AlertCircle className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
        {/* ID + License verification for physicians */}
        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && (
          <div className="flex items-center text-gray-600 gap-3">
            <span className="inline-flex items-center gap-1">
              {renderVerifyIcon(
                userItem.physicianData?.idProofingStatus === 'approved',
                'ID',
                userItem.physicianData?.idProofingStatus
              )}
              <span className="text-xs">ID</span>
            </span>
            {renderCredentialGate(userItem)}
            <span className="inline-flex items-center gap-1">
              <span className="text-xs text-gray-400">Rewards:</span>
              {renderRewardsTierCell(userItem)}
            </span>
          </div>
        )}
        {/* Account Manager for physicians */}
        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && (
          <div className="flex items-center text-gray-600">
            <Building2 className="w-3.5 h-3.5 mr-2 text-gray-400" />
            <span>AM: {getAccountManagerName(userItem)}</span>
          </div>
        )}
        {/* Clinic name for physicians */}
        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && hasClinicName(userItem) && (
          <div className="flex items-center text-gray-600">
            <Building2 className="w-3.5 h-3.5 mr-2 text-gray-400" />
            <span>{userItem.profile.clinicName}</span>
          </div>
        )}
        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && !hasClinicName(userItem) && (
          <div className="flex items-center text-amber-600">
            <AlertCircle className="w-3.5 h-3.5 mr-2" />
            <span className="text-xs">No clinic name set</span>
          </div>
        )}
        {/* Company for distributors and supervisors */}
        {(userItem.role === 'distributor' || userItem.role === 'supervisor') && (userItem.distributorData?.companyName || userItem.supervisorData?.companyName) && (
          <div className="flex items-center text-gray-600">
            <Building2 className="w-3.5 h-3.5 mr-2 text-gray-400" />
            <span>{userItem.role === 'supervisor' ? userItem.supervisorData?.companyName : userItem.distributorData?.companyName}</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-500">{formatDate(userItem.createdAt)}</span>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <button
            onClick={() => handleViewUserDetails(userItem)}
            className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </button>

          {userItem.status === 'pending' && (
            <button
              onClick={() => handleApproveUser(userItem._id, userItem.role)}
              disabled={processingAction === userItem._id}
              className="inline-flex items-center px-2 py-1 border border-transparent rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {processingAction === userItem._id ? (
                <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <UserCheck className="w-3 h-3 mr-1" />
              )}
              Approve
            </button>
          )}

          {(userItem.role === 'physician' || userItem.role === 'physician_readonly') &&
           (user?.role === 'distributor' || user?.role === 'admin') && (() => {
            const proofStatus = userItem.physicianData?.idProofingStatus || 'pending';
            const dotColor = proofStatus === 'approved' ? 'bg-green-500' : proofStatus === 'submitted' ? 'bg-blue-500' : proofStatus === 'rejected' ? 'bg-red-500' : 'bg-yellow-500';
            const statusLabel = proofStatus === 'approved' ? 'Approved' : proofStatus === 'submitted' ? 'Submitted' : proofStatus === 'rejected' ? 'Rejected' : 'Pending';
            return (
              <button
                onClick={() => {
                  setIdProofingPhysicianId(userItem._id);
                  setShowIDProofingViewer(true);
                }}
                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-blue-700 bg-white hover:bg-blue-50"
                title={`ID Proofing: ${statusLabel}`}
              >
                <span className="relative mr-1">
                  <Shield className="w-3 h-3" />
                  <span className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${dotColor}`} />
                </span>
                ID Proof
              </button>
            );
          })()}

          {userItem.status === 'active' && (
            <button
              onClick={() => handleOpenSuspendModal(userItem)}
              disabled={processingAction === userItem._id}
              className="inline-flex items-center px-2 py-1 border border-transparent rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {processingAction === userItem._id ? (
                <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <UserX className="w-3 h-3 mr-1" />
              )}
              Suspend
            </button>
          )}

          {userItem.status === 'suspended' && (
            <button
              onClick={() => handleOpenUnsuspendModal(userItem)}
              disabled={processingAction === userItem._id}
              className="inline-flex items-center px-2 py-1 border border-transparent rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {processingAction === userItem._id ? (
                <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <UserCheck className="w-3 h-3 mr-1" />
              )}
              Reactivate
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderUserTable = () => {
    const showCompany = currentView === 'distributors' || currentView === 'supervisors';
    const showAccountManager = currentView === 'physicians';
    
    return (
      <>
        {/* Mobile: Card view */}
        <div className="md:hidden space-y-3">
          {users.map(renderUserCard)}
        </div>
        
        {/* Desktop: Table view */}
        <div className="hidden md:block data-table-container">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  {currentView === 'pending' && <th>Role</th>}
                  {showAccountManager && <th>Account Manager</th>}
                  {currentView === 'physicians' && <th title="Identity document approved by admin">ID</th>}
                  {currentView === 'physicians' && <th title="Medical license verified by admin">License</th>}
                  {currentView === 'physicians' && <th title="Volume-rewards tier currently in effect (earned from last week's paid volume)">Rewards</th>}
                  {showCompany && <th>Company</th>}
                  {currentView === 'pending' && <th>Details</th>}
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((userItem) => (
                  <tr key={userItem._id}>
                    {/* Name with NPI underneath for physicians */}
                    <td className="cell-primary">
                      <div className="cell-inline">
                        <div className="flex-shrink-0 mr-3">
                          {getRoleIcon(userItem.role)}
                        </div>
                        <div className="cell-stack">
                          <span className="cell-stack-value inline-flex items-center gap-2">
                            {userItem.profile.firstName} {userItem.profile.lastName}
                            {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && (
                              <OpenTicketsBadge count={physicianTicketCounts[userItem._id] || 0} />
                            )}
                          </span>
                          {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && userItem.physicianData?.npiNumber && (
                            <span className="cell-stack-label font-mono">NPI: {userItem.physicianData.npiNumber}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="cell-secondary">
                      <div className="cell-stack">
                        <span className="cell-truncate" title={userItem.email}>{userItem.email}</span>
                        <span className="cell-stack-label inline-flex items-center">
                          {userItem.profile.phone}
                          {userItem.role === 'distributor' && isFakePhoneNumber(userItem.profile.phone) && (
                            <span title="Phone number appears invalid">
                              <AlertCircle className="w-3.5 h-3.5 ml-1 text-amber-500" />
                            </span>
                          )}
                        </span>
                        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && hasClinicName(userItem) && (
                          <span className="cell-stack-label text-gray-500 inline-flex items-center">
                            <Building2 className="w-3 h-3 mr-1" />
                            {userItem.profile.clinicName}
                          </span>
                        )}
                        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') && !hasClinicName(userItem) && (
                          <span className="cell-stack-label text-amber-600 inline-flex items-center">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No clinic name
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Role (pending view only) */}
                    {currentView === 'pending' && (
                      <td>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          userItem.role === 'distributor'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {userItem.role === 'distributor' ? 'Account Manager' : 'Physician'}
                        </span>
                      </td>
                    )}

                    {/* Account Manager (physicians view) */}
                    {showAccountManager && (
                      <td className="cell-secondary">
                        {getAccountManagerName(userItem)}
                      </td>
                    )}

                    {/* ID + License verification (physicians view only) */}
                    {currentView === 'physicians' && (
                      <>
                        <td>
                          {renderVerifyIcon(
                            userItem.physicianData?.idProofingStatus === 'approved',
                            'ID',
                            userItem.physicianData?.idProofingStatus
                          )}
                        </td>
                        <td>
                          {renderCredentialGateCell(userItem)}
                        </td>
                        <td>
                          {renderRewardsTierCell(userItem)}
                        </td>
                      </>
                    )}

                    {/* Company (distributors/supervisors view) */}
                    {showCompany && (
                      <td className="cell-secondary">
                        {(currentView === 'supervisors'
                          ? userItem.supervisorData?.companyName
                          : userItem.distributorData?.companyName) || <span className="cell-muted">—</span>}
                      </td>
                    )}

                    {/* Details (pending view - shows NPI or Company based on role) */}
                    {currentView === 'pending' && (
                      <td className="cell-secondary">
                        {userItem.role === 'distributor'
                          ? userItem.distributorData?.companyName || <span className="cell-muted">—</span>
                          : userItem.physicianData?.npiNumber ? <span className="font-mono">NPI: {userItem.physicianData.npiNumber}</span> : <span className="cell-muted">—</span>
                        }
                      </td>
                    )}

                    {/* Status */}
                    <td>
                      {getStatusBadge(userItem.status)}
                    </td>

                    {/* Created */}
                    <td className="cell-secondary">
                      {formatDate(userItem.createdAt)}
                    </td>

                    {/* Actions */}
                    <td className="cell-actions">
                      <div className="table-actions">
                        {/* View Details */}
                        <button
                          onClick={() => handleViewUserDetails(userItem)}
                          className="table-action-btn table-action-btn-secondary"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          <span className="hidden lg:inline">View</span>
                        </button>

                        {/* Approve (pending only) */}
                        {userItem.status === 'pending' && (
                          <button
                            onClick={() => handleApproveUser(userItem._id, userItem.role)}
                            disabled={processingAction === userItem._id}
                            className="table-action-btn table-action-btn-success"
                          >
                            {processingAction === userItem._id ? (
                              <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <UserCheck className="w-3 h-3 mr-1" />
                            )}
                            Approve
                          </button>
                        )}

                        {/* ID Proofing (physicians only) */}
                        {(userItem.role === 'physician' || userItem.role === 'physician_readonly') &&
                         (user?.role === 'distributor' || user?.role === 'admin') && (() => {
                          const proofStatus = userItem.physicianData?.idProofingStatus || 'pending';
                          const dotColor = proofStatus === 'approved' ? 'bg-green-500' : proofStatus === 'submitted' ? 'bg-blue-500' : proofStatus === 'rejected' ? 'bg-red-500' : 'bg-yellow-500';
                          const statusLabel = proofStatus === 'approved' ? 'Approved' : proofStatus === 'submitted' ? 'Submitted' : proofStatus === 'rejected' ? 'Rejected' : 'Pending';
                          return (
                            <button
                              onClick={() => {
                                setIdProofingPhysicianId(userItem._id);
                                setShowIDProofingViewer(true);
                              }}
                              className="table-action-btn table-action-btn-primary"
                              title={`ID Proofing: ${statusLabel}`}
                            >
                              <span className="relative mr-1">
                                <Shield className="w-3 h-3" />
                                <span className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${dotColor}`} />
                              </span>
                              <span className="hidden lg:inline">ID Proof</span>
                            </button>
                          );
                        })()}

                        {/* Suspend (active only) */}
                        {userItem.status === 'active' && (
                          <button
                            onClick={() => handleOpenSuspendModal(userItem)}
                            disabled={processingAction === userItem._id}
                            className="table-action-btn table-action-btn-danger"
                          >
                            {processingAction === userItem._id ? (
                              <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <UserX className="w-3 h-3 mr-1" />
                            )}
                            <span className="hidden lg:inline">Suspend</span>
                          </button>
                        )}

                        {/* Reactivate (suspended only) */}
                        {userItem.status === 'suspended' && (
                          <button
                            onClick={() => handleOpenUnsuspendModal(userItem)}
                            disabled={processingAction === userItem._id}
                            className="table-action-btn table-action-btn-success"
                          >
                            {processingAction === userItem._id ? (
                              <div className="animate-spin h-3 w-3 mr-1 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <UserCheck className="w-3 h-3 mr-1" />
                            )}
                            <span className="hidden lg:inline">Reactivate</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  if (currentView === 'admins') {
    return <AdminsListView />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
          <p className="text-gray-600">{getPageDescription()}</p>
        </div>
        <div className="flex items-center space-x-3">
          {(user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'distributor') && (
            <button
              onClick={() => setShowInvitationsViewer(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <MailCheck className="w-4 h-4 mr-2" />
              View Invitations
            </button>
          )}
          {(user?.role === 'admin' || user?.role === 'supervisor' || (user?.role === 'distributor' && currentView === 'physicians')) && (
            <button
              onClick={() => setShowCreateUser(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {currentView === 'supervisors' ? 'Supervisor' : currentView === 'distributors' ? 'Account Manager' : 'Physician'}
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards - Only show for admin users, context-aware based on current view */}
      {stats && user?.role === 'admin' && (
        <div className={`grid gap-3 lg:gap-4 ${
          currentView === 'distributors'
            ? 'grid-cols-2 md:grid-cols-4'
            : currentView === 'pending'
            ? 'grid-cols-2 md:grid-cols-3'
            : 'grid-cols-2 md:grid-cols-4'
        }`}>
          {/* Physicians View Stats */}
          {currentView === 'physicians' && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Stethoscope className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Physicians</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.physicians}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Active</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.activeUsers}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 lg:h-8 lg:w-8 text-yellow-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Pending</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.pendingUsers}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <XCircle className="h-6 w-6 lg:h-8 lg:w-8 text-red-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Suspended</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.suspendedUsers}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Account Managers View Stats */}
          {currentView === 'distributors' && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Building2 className="h-6 w-6 lg:h-8 lg:w-8 text-purple-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total AMs</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.distributors}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Stethoscope className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Physicians</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.physicians}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 lg:h-8 lg:w-8 text-gray-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Avg Physicians/AM</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">
                      {stats.distributors > 0 ? (stats.physicians / stats.distributors).toFixed(1) : '0'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Active Users</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.activeUsers}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pending Approvals View Stats */}
          {currentView === 'pending' && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 lg:h-8 lg:w-8 text-yellow-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Pending</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.pendingUsers}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Users</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-600" />
                  </div>
                  <div className="ml-2 lg:ml-4 min-w-0">
                    <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">Active</p>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.activeUsers}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={currentView === 'physicians' 
                  ? "Search by name, email, NPI, clinic, phone..." 
                  : "Search users..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Account Manager Filter - Admin and Supervisor on physicians view */}
            {currentView === 'physicians' && (user?.role === 'admin' || user?.role === 'supervisor') && (
              <div className="min-w-[240px]">
                <AsyncSearchSelect<User>
                  fetchPage={({ page, pageSize: size, search, signal }) =>
                    api.searchUsersPage({ role: 'distributor', page, pageSize: size, search, signal })}
                  getKey={am => am._id}
                  getLabel={am => am.distributorData?.companyName || `${am.profile?.firstName} ${am.profile?.lastName}`}
                  getDescription={am => am.distributorData?.companyName ? `${am.profile?.firstName} ${am.profile?.lastName}` : am.email}
                  value={selectedAm}
                  onChange={setSelectedAm}
                  placeholder="All Account Managers"
                  noun="account managers"
                  aria-label="Filter by account manager"
                />
              </div>
            )}
            {/* Status Filter */}
            {currentView !== 'pending' && (
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}
          </div>
        </div>
        {/* Active filters indicator */}
        {(searchTerm || statusFilter || amFilter) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {amFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                AM: {selectedAm?.distributorData?.companyName || `${selectedAm?.profile?.firstName ?? ''} ${selectedAm?.profile?.lastName ?? ''}`.trim() || 'Selected'}
                <button onClick={() => setSelectedAm(null)} className="hover:text-purple-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')} className="hover:text-green-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setSelectedAm(null);
              }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* User List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {currentView === 'pending' ? 'pending users' : currentView} found
          </h3>
          <p className="text-gray-500">
            {currentView === 'pending' 
              ? 'No users are awaiting approval.'
              : `No ${currentView} match your search criteria.`
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {renderUserTable()}
          {/* Pagination Controls */}
          {(currentView === 'physicians' || currentView === 'distributors' || currentView === 'supervisors') && renderPaginationControls()}
        </div>
      )}

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onSuccess={() => {
          // Refresh the user list after successful invitation
          fetchData();
          setShowCreateUser(false);
        }}
        role={currentView === 'supervisors' ? 'supervisor' : currentView === 'distributors' ? 'distributor' : 'physician'}
      />

      {/* Invitations Viewer */}
      {showInvitationsViewer && (
        <InvitationsViewer
          onClose={() => setShowInvitationsViewer(false)}
        />
      )}

      {/* Physician ID Proofing Viewer */}
      {idProofingPhysicianId && (
        <PhysicianIDProofingViewer
          physicianId={idProofingPhysicianId}
          isOpen={showIDProofingViewer}
          onClose={() => {
            setShowIDProofingViewer(false);
            setIdProofingPhysicianId(null);
          }}
          onActionComplete={() => {
            // Refresh the user list after approval/rejection
            fetchData();
          }}
        />
      )}

      {/* Access & Pricing Modal for Admins */}
      {showAccessModal && selectedUser && (
        <UserAccessModal
          subject={selectedUser}
          onClose={handleCloseAccessModal}
          onSubjectPatched={(patch) => setSelectedUser(prev => (prev ? ({ ...prev, ...patch } as any) : prev))}
          onImpersonate={handleOpenImpersonateModal}
          {...hierarchyMoves.modalProps}
        />
      )}

      {hierarchyMoves.modals}

      {/* Impersonation Confirmation Modal */}
      {showImpersonateModal && selectedUser && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Start Impersonation</h3>
                  <p className="text-sm text-gray-500">You will view Bloom as this user</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4">
              {impersonationError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm text-red-800">{impersonationError}</div>
                </div>
              )}
              
              <div className="space-y-4">
                {/* Target User Info */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">You are about to impersonate:</div>
                  <div className="font-medium text-gray-900">
                    {selectedUser.profile.firstName} {selectedUser.profile.lastName}
                  </div>
                  <div className="text-sm text-gray-600">{selectedUser.email}</div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      selectedUser.role === 'distributor' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedUser.role === 'distributor' ? 'Account Manager' : 
                       selectedUser.role === 'physician' ? 'Physician' : 
                       selectedUser.role === 'physician_readonly' ? 'Physician (Read-only)' : selectedUser.role}
                    </span>
                  </div>
                </div>

                {/* Reason Input */}
                <div>
                  <label htmlFor="impersonation-reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for impersonation <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="impersonation-reason"
                    value={impersonationReason}
                    onChange={(e) => setImpersonationReason(e.target.value)}
                    placeholder="e.g., Debugging order issue, Customer support request..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Security Warning */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-amber-800 mb-1">Security Notice</div>
                      <ul className="text-amber-700 space-y-1">
                        <li>• All actions will be logged for audit purposes</li>
                        <li>• Your admin identity will be recorded</li>
                        <li>• Session will be visible on the Impersonation Dashboard</li>
                        <li>• A banner will display while impersonating</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelImpersonation}
                disabled={impersonationLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartImpersonation}
                disabled={impersonationLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {impersonationLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Starting...
                  </>
                ) : (
                  <>
                    <UserCog className="w-4 h-4 mr-2" />
                    Start Impersonation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Confirmation Modal */}
      {showSuspendModal && userToSuspend && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Suspend User</h3>
                  <p className="text-sm text-gray-500">This action can be reversed later</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="space-y-4">
                {/* User Info */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    {getRoleIcon(userToSuspend.role)}
                    <div>
                      <div className="font-medium text-gray-900">
                        {userToSuspend.profile.firstName} {userToSuspend.profile.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{userToSuspend.email}</div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          userToSuspend.role === 'distributor' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {userToSuspend.role === 'distributor' ? 'Account Manager' : 'Physician'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-amber-800 mb-1">What happens when you suspend?</div>
                      <ul className="text-amber-700 space-y-1">
                        <li>• User will be unable to log in</li>
                        <li>• All active sessions will be terminated</li>
                        {userToSuspend.role === 'distributor' && (
                          <li>• Physicians in their network will lose access to catalogs</li>
                        )}
                        <li>• You can reactivate this account later</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelSuspend}
                disabled={processingAction === userToSuspend._id}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSuspend}
                disabled={processingAction === userToSuspend._id}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {processingAction === userToSuspend._id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Suspending...
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Suspend User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsuspend User Confirmation Modal */}
      {showUnsuspendModal && userToUnsuspend && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reactivate User</h3>
                  <p className="text-sm text-gray-500">Restore access for this account</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-4">
                {/* User Info */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    {getRoleIcon(userToUnsuspend.role)}
                    <div>
                      <div className="font-medium text-gray-900">
                        {userToUnsuspend.profile.firstName} {userToUnsuspend.profile.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{userToUnsuspend.email}</div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          userToUnsuspend.role === 'distributor'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {userToUnsuspend.role === 'distributor' ? 'Account Manager' : 'Physician'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <UserCheck className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-green-800 mb-1">What happens when you reactivate?</div>
                      <ul className="text-green-700 space-y-1">
                        <li>• User will be able to log in again</li>
                        <li>• Full account access will be restored</li>
                        {userToUnsuspend.role === 'distributor' && (
                          <li>• Physicians in their network will regain access to catalogs</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelUnsuspend}
                disabled={processingAction === userToUnsuspend._id}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUnsuspend}
                disabled={processingAction === userToUnsuspend._id}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {processingAction === userToUnsuspend._id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Reactivating...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Reactivate User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserManagement;